import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon, CheckCircleIcon } from '@heroicons/react/20/solid';

interface PreOrder {
    id: number;
    code: string;
    marca: string;
    color: string;
    talla: string;
    saleId?: number;
    saleFolio?: string;
    customerName?: string;
    customerPhone?: string;
    total?: number;
    amountPaid?: number;
    shippingCost?: number;
    isOnlineStore?: boolean;
}

interface PreOrderDeliveryWarningModalProps {
    isOpen: boolean;
    onClose: () => void;
    preOrders: PreOrder[];
}

export const PreOrderDeliveryWarningModal: React.FC<PreOrderDeliveryWarningModalProps> = ({
    isOpen,
    onClose,
    preOrders
}) => {
    const [notifiedIds, setNotifiedIds] = useState<Set<number>>(new Set());
    const [deliveryModes, setDeliveryModes] = useState<Record<number, 'physical' | 'online'>>({});

    if (!isOpen) return null;

    const handleNotifyClick = (id: number) => {
        setNotifiedIds(prev => new Set([...prev, id]));
    };

    return createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl flex flex-col max-h-[90vh]">
                <div className="flex items-center justify-between p-6 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-600">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0" />
                            </svg>
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">Pre-orders Recibidos</h2>
                            <p className="text-sm text-gray-500">Notifica a los clientes que su mercancía ya está lista para entrega o envío</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-full hover:bg-gray-100"
                    >
                        <XMarkIcon className="w-6 h-[1.5rem]" />
                    </button>
                </div>

                <div className="p-6 overflow-y-auto custom-scrollbar">
                    <div className="space-y-4">
                        {preOrders.map((po, index) => {
                            // Calculate missing payment if any
                            const pendingAmount = (po.total || 0) - (po.amountPaid || 0);
                            const isNotified = notifiedIds.has(po.id);

                            // Detectar modo automático basado en costo de envío o tienda en línea
                            const defaultMode = (po.shippingCost && po.shippingCost > 0) || po.isOnlineStore ? 'online' : 'physical';
                            const currentMode = deliveryModes[po.id] || defaultMode;

                            // Construct WhatsApp message
                            const storeName = "MariaRegna";
                            let message = "";

                            if (currentMode === 'online') {
                                message = `¡Hola ${po.customerName || ''}! 🌟\n\nTe informamos que tu pedido especial (${po.marca} ${po.color}) vinculado a la nota con folio ${po.saleFolio} ya llegó a nuestro almacén principal y está listo para su envío por paquetería.`;
                                if (pendingAmount > 0) {
                                    message += `\n\nTe recordamos que tienes un saldo pendiente de $${pendingAmount.toFixed(2)}.`;
                                }
                                message += `\n\nEn breve te estaremos enviando tu número de guía de paquetería para que puedas rastrear su envío hasta tu domicilio.`;
                                message += `\n\n¡Gracias por tu preferencia! ${storeName}`;
                            } else {
                                message = `¡Hola ${po.customerName || ''}! 🌟\n\nTe informamos que tu pedido especial (${po.marca} ${po.color}) vinculado a la nota con folio ${po.saleFolio} ya llegó a nuestra tienda y está listo para entregarse.`;
                                if (pendingAmount > 0) {
                                    message += `\n\nTe recordamos que tienes un saldo pendiente de $${pendingAmount.toFixed(2)}.`;
                                }
                                message += `\n\nHorario para entrega: Lunes a Viernes únicamente de 11:00 am a 7:30 pm.`;
                                message += `\n\n¡Te esperamos pronto! ${storeName}`;
                            }

                            const encodedMessage = encodeURIComponent(message);
                            const waLink = po.customerPhone
                                ? `https://wa.me/${po.customerPhone.replace(/[\s\-\+]/g, '')}?text=${encodedMessage}`
                                : null;

                            return (
                                <div key={`${po.id}-${index}`} className={`flex flex-col sm:flex-row sm:items-center justify-between p-5 border rounded-xl transition-all gap-4 ${isNotified ? 'bg-green-50/50 border-green-100' : 'bg-gray-50/50 border-gray-100 hover:bg-gray-50'}`}>
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="font-semibold text-gray-900">{po.customerName || 'Cliente anónimo'}</span>
                                            {po.saleFolio && (
                                                <span className="text-xs bg-indigo-100 text-indigo-700 px-2.5 py-0.5 rounded-full font-medium">Nota: {po.saleFolio}</span>
                                            )}
                                            {isNotified && (
                                                <span className="flex items-center gap-1 text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">
                                                    <CheckCircleIcon className="w-3 h-3" />
                                                    Notificado
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-sm text-gray-600 mt-1">
                                            {po.marca} - {po.code} <br />
                                            Color: {po.color} | Talla: {po.talla}
                                        </div>
                                        {po.shippingCost && po.shippingCost > 0 && (
                                            <div className="mt-2 text-xs font-semibold text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-md inline-block">
                                                🚚 Incluye envío (${po.shippingCost.toFixed(2)})
                                            </div>
                                        )}
                                        {pendingAmount > 0 && (
                                            <div className="mt-2 text-xs font-semibold text-red-600 bg-red-50 px-2.5 py-1 rounded-md inline-block ml-2">
                                                Saldo pendiente: ${pendingAmount.toFixed(2)}
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex flex-col items-start sm:items-end gap-2">
                                        <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg text-xs font-medium">
                                            <button
                                                type="button"
                                                onClick={() => setDeliveryModes(prev => ({ ...prev, [po.id]: 'physical' }))}
                                                className={`px-2.5 py-1 rounded-md transition-all ${currentMode === 'physical' ? 'bg-white text-gray-900 shadow-sm font-semibold' : 'text-gray-500 hover:text-gray-700'}`}
                                            >
                                                🏢 Tienda Física
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setDeliveryModes(prev => ({ ...prev, [po.id]: 'online' }))}
                                                className={`px-2.5 py-1 rounded-md transition-all ${currentMode === 'online' ? 'bg-indigo-600 text-white shadow-sm font-semibold' : 'text-gray-500 hover:text-gray-700'}`}
                                            >
                                                🌐 Tienda en Línea
                                            </button>
                                        </div>
                                        {waLink ? (
                                            <a
                                                href={waLink}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={() => handleNotifyClick(po.id)}
                                                className={`flex items-center justify-center gap-2 px-5 py-2 text-sm font-medium rounded-lg transition-all shadow-sm w-full sm:w-auto ${isNotified 
                                                    ? 'bg-white border border-green-200 text-green-600 hover:bg-green-50' 
                                                    : 'bg-[#25D366] text-white hover:bg-[#128C7E]'}`}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                                    <path d="M13.601 2.326A7.85 7.85 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.9 7.9 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.9 7.9 0 0 0 13.6 2.326zM7.994 14.521a6.6 6.6 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.56 6.56 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592m3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.73.73 0 0 0-.529.247c-.182.198-.691.677-.691 1.654s.71 1.916.81 2.04.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232" />
                                                </svg>
                                                {isNotified ? 'Re-enviar' : 'Avisar'}
                                            </a>
                                        ) : (
                                            <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1.5 rounded-md">Sin teléfono</span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="p-6 border-t border-gray-100 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-6 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-colors font-medium shadow-md shadow-gray-200"
                    >
                        Entendido
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};
